// src/v3/ProductSelectCards/CardSelectorContents.tsx
import { useContext as useContext2, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cx as cx4 } from "class-variance-authority";

// src/v3/Typography/index.tsx
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
var typography = cva("", {
  variants: {
    variant: {
      h1: "ui-text-[2.5rem] ui-tracking-tight md:ui-text-[3.75rem] lg:ui-text-[5rem]",
      h2: "ui-text-[2.25rem] ui-tracking-normal md:ui-text-[3rem] lg:ui-text-[4.25rem] lg:ui-tracking-[-2px]",
      h3: "ui-text-[2rem] ui-tracking-normal md:ui-text-[2.5rem] lg:ui-text-[3.5rem]",
      h4: "ui-text-[1.75rem] ui-tracking-normal md:ui-text-[2rem] lg:ui-text-[2.5rem]",
      h5: "ui-text-[1.25rem] ui-tracking-normal lg:ui-leading-10",
      h6: "ui-text-[1.25rem] ui-tracking-normal",
      "body-large": "ui-text-[1.125rem] ui-tracking-normal",
      "title-large": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-8xl xl:ui-text-[7.5rem]",
      "title-small": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-[5rem]",
      base: "ui-text-base ui-tracking-normal",
      "body-small": "ui-text-[0.875rem] ui-tracking-normal",
      "eyebrow-large": "ui-text-base",
      "eyebrow-small": "ui-text-[0.75rem]",
      unset: ""
    },
    align: {
      left: "ui-text-left",
      center: "ui-text-center",
      right: "ui-text-right",
      justify: "ui-text-justify",
      centerMdLeft: "ui-text-center md:ui-text-left",
      centerLgLeft: "ui-text-center lg:ui-text-left"
    },
    color: {
      light: "ui-text-sandstone-200",
      dark: "ui-text-sandstone-500",
      disabled: "ui-text-gray-300",
      error: "ui-text-red-600",
      gray: "ui-text-gray-400",
      success: "ui-text-green-600",
      inherit: "ui-text-inherit",
      unset: ""
    },
    font: {
      sans: "ui-font-sans",
      serif: "ui-font-serif",
      "serif-italic": "ui-font-serif ui-italic"
    },
    height: {
      none: "ui-leading-none",
      tight: "ui-leading-tight",
      snug: "ui-leading-snug",
      normal: "ui-leading-normal",
      relaxed: "ui-leading-relaxed",
      loose: "ui-leading-loose",
      heading: "ui-leading-heading"
    },
    weight: {
      thin: "ui-font-thin",
      extralight: "ui-font-extralight",
      light: "ui-font-light",
      normal: "ui-font-normal",
      medium: "ui-font-medium",
      semibold: "ui-font-semibold",
      bold: "ui-font-bold",
      extrabold: "ui-font-extrabold",
      black: "ui-font-black"
    }
  },
  defaultVariants: {
    variant: "base",
    height: "normal",
    weight: "normal",
    font: "sans"
  }
});
function Typography({
  align = "left",
  Element = "p",
  children,
  className,
  color = "dark",
  font = "sans",
  height,
  variant = "base",
  weight = "normal",
  style,
  ...props
}) {
  let _weight = weight;
  if (variant === "h1" || variant === "h2" || variant === "h3" || variant === "h4") {
    _weight = "light";
  }
  return /* @__PURE__ */ jsx(Element, {
    className: typography({
      align,
      color,
      height,
      variant,
      weight: _weight,
      font,
      className
    }),
    style,
    ...props,
    children
  });
}
Typography.displayName = "Typography";

// src/v3/ChooseButton/ChooseButtonCheck.tsx
import { cx as cx2 } from "class-variance-authority";

// src/icons/CircularFillCheckmarkIcon.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function CircularFillCheckmarkIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 24,
    viewBox: "0 0 24 24",
    width: 24,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx2("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx2("circle", {
        cx: 12,
        cy: 12,
        fill: "currentColor",
        r: 11,
        stroke: "currentColor"
      }),
      /* @__PURE__ */ jsx2("path", {
        d: "M7.75 12.5L10.9167 15.6666L17.25 9.33331",
        stroke: "white",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: 2
      })
    ]
  });
}

// src/react-intl/FormattedMessage.tsx
import { FormattedMessage as ReactIntlFormattedMessage } from "react-intl";

// src/v3/BodyLink/index.tsx
import { forwardRef, useContext } from "react";
import { cx } from "class-variance-authority";

// src/contexts/ComponentProvider.tsx
import { createContext, useMemo } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var GLOBAL_KEY = "__@jouzen/ecom-components:ComponentContext__";
function getOrCreateContext() {
  const g = globalThis;
  if (!g[GLOBAL_KEY])
    g[GLOBAL_KEY] = createContext({});
  return g[GLOBAL_KEY];
}
var Context = getOrCreateContext();
function ComponentProvider({
  children,
  LinkComponent,
  ImageComponent
}) {
  const contextValue = useMemo(
    () => ({ ImageComponent, LinkComponent }),
    [ImageComponent, LinkComponent]
  );
  return /* @__PURE__ */ jsx3(Context.Provider, {
    value: contextValue,
    children
  });
}
ComponentProvider.displayName = "ComponentProvider";

// src/v3/BodyLink/index.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var BodyLink = forwardRef(
  function BodyLink2({
    children,
    className,
    color = "sandstone-500",
    external = false,
    href = "",
    onClick,
    ...rest
  }, ref) {
    const context = useContext(Context);
    const LinkComponent = external || !context.LinkComponent ? "a" : context.LinkComponent;
    return /* @__PURE__ */ jsx4(LinkComponent, {
      "data-cy": "body_link",
      ...rest,
      className: cx(`ui-underline ui-text-${color}`, className),
      href,
      onClick: (evt) => onClick == null ? void 0 : onClick(evt),
      ref,
      children
    });
  }
);
BodyLink.displayName = "BodyLink";

// src/react-intl/htmlFormatting.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var htmlFormatting = {
  br: /* @__PURE__ */ jsx5("br", {}),
  w: /* @__PURE__ */ jsx5("wbr", {}),
  r: String.fromCharCode(174),
  tm: String.fromCharCode(8482),
  break: function breakTag() {
    return /* @__PURE__ */ jsx5("br", {});
  },
  strong: function strong(chunks) {
    return /* @__PURE__ */ jsx5("strong", {
      children: chunks
    });
  },
  b: function b(chunks) {
    return /* @__PURE__ */ jsx5("strong", {
      children: chunks
    });
  },
  em: function em(chunks) {
    return /* @__PURE__ */ jsx5("em", {
      children: chunks
    });
  },
  i: function i(chunks) {
    return /* @__PURE__ */ jsx5("em", {
      children: chunks
    });
  },
  u: function u(chunks) {
    return /* @__PURE__ */ jsx5("span", {
      className: "ui-underline",
      children: chunks
    });
  },
  sup: function sup(chunks) {
    return /* @__PURE__ */ jsx5("sup", {
      className: "ui-font-serif ui-italic ui-text-blue-200 ui-font-semibold ui-tracking-wider",
      children: chunks
    });
  },
  li: function li(chunks) {
    return /* @__PURE__ */ jsx5("li", {
      children: chunks
    });
  },
  ExternalLink: function ExternalLink(href) {
    return /* @__PURE__ */ jsx5(BodyLink, {
      href,
      rel: "noreferrer",
      target: "_blank",
      children: href
    });
  },
  EmailLink: function EmailLink(email) {
    return /* @__PURE__ */ jsx5(BodyLink, {
      href: `mailto:${email}`,
      children: email
    });
  }
};

// src/react-intl/stringFormatting.ts
var stringFormatting = Object.entries(
  htmlFormatting
).reduce((obj, [key, value]) => {
  const type = typeof value;
  if (type === "string") {
    return { ...obj, [key]: value };
  } else if (type === "function") {
    return { ...obj, [key]: (chunks) => chunks };
  }
  return { ...obj, [key]: "" };
}, {});

// src/react-intl/getLocaleFormatting.tsx
var getLocaleFormatting = (options = {}) => {
  const { stripHtml } = options;
  if (stripHtml) {
    return stringFormatting;
  }
  return htmlFormatting;
};
getLocaleFormatting.displayName = "getLocaleFormatting";
var getLocaleFormatting_default = getLocaleFormatting;

// src/react-intl/FormattedMessage.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function FormattedMessage({
  id,
  values: optionalValues,
  ...props
}) {
  const localeFormatting = getLocaleFormatting_default();
  const values = {
    ...localeFormatting,
    ...optionalValues
  };
  return /* @__PURE__ */ jsx6(ReactIntlFormattedMessage, {
    id,
    values,
    ...props
  });
}
function t(id, values) {
  return /* @__PURE__ */ jsx6(FormattedMessage, {
    id,
    values
  });
}
FormattedMessage.displayName = "FormattedMessage";

// src/v3/ChooseButton/ChooseButtonCheck.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
function ChooseButtonCheck({
  className = "ui-size-6 ui-text-slate-200",
  chosen
}) {
  return /* @__PURE__ */ jsx7(CircularFillCheckmarkIcon, {
    "aria-hidden": !chosen,
    "aria-label": t("pdp_selected_check_label"),
    className: cx2(
      "ui-inline-flex ui-self-center",
      {
        "ui-invisible": !chosen,
        "ui-visible": chosen
      },
      className
    ),
    "data-cy": "choose-btn-check"
  });
}
ChooseButtonCheck.displayName = "ChooseButtonCheck";
var ChooseButtonCheck_default = ChooseButtonCheck;

// src/v3/RadioSelectorGroup/index.tsx
import {
  createContext as createContext2
} from "react";
import { useRadioGroup } from "react-aria";
import { useRadioGroupState } from "react-stately";
import { cx as cx3 } from "class-variance-authority";
import { jsx as jsx8, jsxs as jsxs2 } from "react/jsx-runtime";
var RadioSelectorGroupContext = createContext2(
  null
);
function RadioSelectorGroup(props) {
  const {
    cardSelectHeader: CardSelectHeader,
    children,
    hideLabel = false,
    label,
    description,
    infoModal,
    onChange: _onChange,
    ...rest
  } = props;
  const state = useRadioGroupState(props);
  const { radioGroupProps, labelProps, descriptionProps } = useRadioGroup(
    props,
    state
  );
  return /* @__PURE__ */ jsxs2("div", {
    ...rest,
    ...radioGroupProps,
    children: [
      hideLabel ? /* @__PURE__ */ jsx8("span", {
        ...labelProps,
        className: cx3(labelProps.className, "ui-sr-only"),
        children: label
      }) : null,
      CardSelectHeader ? /* @__PURE__ */ jsx8(CardSelectHeader, {
        description,
        descriptionProps,
        infoModal,
        label,
        labelProps
      }) : null,
      /* @__PURE__ */ jsx8("div", {
        className: "ui-relative ui-flex ui-w-full ui-flex-col ui-gap-2",
        children: /* @__PURE__ */ jsx8(RadioSelectorGroupContext.Provider, {
          value: state,
          children
        })
      })
    ]
  });
}
RadioSelectorGroup.displayName = "RadioSelectorGroup";

// src/v3/ProductSelectCards/CardSelectorContents.tsx
import { Fragment, jsx as jsx9, jsxs as jsxs3 } from "react/jsx-runtime";
function CardSelectorContents({
  additionalContent,
  childIds = [],
  description,
  id,
  showContentOnExpand,
  title
}) {
  var _a;
  const state = useContext2(RadioSelectorGroupContext);
  const isSelected = (state == null ? void 0 : state.selectedValue) === id;
  const animateContent = showContentOnExpand && (childIds.includes((_a = state == null ? void 0 : state.selectedValue) != null ? _a : "") || isSelected);
  const showContent = animateContent != null ? animateContent : !showContentOnExpand;
  const [animationComplete, setAnimationComplete] = useState(isSelected);
  const motionProps = {
    animate: { height: "auto", opacity: 1 },
    exit: {
      height: animateContent ? 0 : "auto",
      opacity: animateContent ? 0 : 1
    },
    initial: {
      height: animateContent ? 0 : "auto",
      opacity: animateContent ? 0 : 1
    },
    transition: { duration: 0.3 },
    onAnimationComplete: ({ opacity }) => {
      setAnimationComplete(opacity === 1);
    }
  };
  return /* @__PURE__ */ jsxs3(Fragment, {
    children: [
      /* @__PURE__ */ jsxs3("div", {
        className: "ui-flex ui-justify-between ui-gap-2 ui-items-center ui-w-full ui-text-gray-600",
        children: [
          /* @__PURE__ */ jsxs3("div", {
            children: [
              /* @__PURE__ */ jsx9(Typography, {
                variant: "body-small",
                weight: "black",
                children: title
              }),
              /* @__PURE__ */ jsx9(AnimatePresence, {
                children: showContent && description ? /* @__PURE__ */ jsx9(motion.div, {
                  ...motionProps,
                  children: /* @__PURE__ */ jsx9(Typography, {
                    variant: "body-small",
                    children: description
                  }, id)
                }, id) : null
              })
            ]
          }),
          /* @__PURE__ */ jsx9("div", {
            className: "ui-flex ui-justify-end",
            children: /* @__PURE__ */ jsx9(ChooseButtonCheck_default, {
              chosen: isSelected,
              className: cx4("ui-size-5 ui-transition-opacity", {
                "ui-opacity-0": !animationComplete,
                "ui-opacity-100": animationComplete
              })
            })
          })
        ]
      }),
      /* @__PURE__ */ jsx9(AnimatePresence, {
        children: showContent && additionalContent ? /* @__PURE__ */ jsx9(motion.div, {
          ...motionProps,
          children: /* @__PURE__ */ jsx9("div", {
            className: "ui-mt-2",
            children: additionalContent
          })
        }, id) : null
      })
    ]
  });
}
CardSelectorContents.displayName = "CardSelectorContents";

// src/v3/ProductSelectCards/CardAdditionalContent.tsx
import { useId } from "react";
import { cx as cx6 } from "class-variance-authority";

// src/v3/List/index.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx10 } from "react/jsx-runtime";
var LIST_TYPE_OL = "ol";
var LIST_TYPE_UL = "ul";
var listVar = cva2("[&>li]ui-list-[inherit]", {
  variants: {
    Element: {
      [LIST_TYPE_OL]: "",
      [LIST_TYPE_UL]: ""
    },
    condensed: {
      true: "",
      false: ""
    },
    unstyled: {
      true: "",
      false: ""
    }
  },
  compoundVariants: [
    {
      condensed: true,
      unstyled: true,
      className: "ui-my-1 [&>li]ui-my-1 ui-list-none"
    },
    {
      condensed: true,
      unstyled: false,
      className: "ui-my-1 [&>li]ui-my-1"
    },
    {
      condensed: false,
      unstyled: true,
      className: "ui-list-none"
    },
    {
      condensed: false,
      unstyled: false,
      className: "[&>li]ui-my-2"
    },
    {
      Element: LIST_TYPE_OL,
      unstyled: false,
      className: "ui-list-decimal ui-pl-8"
    },
    {
      Element: LIST_TYPE_UL,
      unstyled: false,
      className: "ui-list-disc ui-pl-8"
    }
  ]
});
function List({
  Element = "ul",
  condensed = false,
  unstyled = false,
  children,
  className = "",
  ...props
}) {
  return /* @__PURE__ */ jsx10(Typography, {
    Element,
    className: listVar({ className, condensed, Element, unstyled }),
    ...props,
    children
  });
}
List.displayName = "List";

// src/v3/CheckmarkListItem/index.tsx
import { cx as cx5 } from "class-variance-authority";

// src/icons/CheckmarkIcon.tsx
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
function CheckmarkIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs4("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 10,
    viewBox: "0 0 14 10",
    width: 14,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx11("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx11("path", {
        clipRule: "evenodd",
        d: "M13.707.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L5 7.586 12.293.293a1 1 0 0 1 1.414 0Z",
        fill: "currentColor",
        fillRule: "evenodd"
      })
    ]
  });
}

// src/v3/ListItem/index.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function ListItem({ children, ...props }) {
  return /* @__PURE__ */ jsx12(Typography, {
    ...props,
    Element: "li",
    weight: "normal",
    children
  });
}
ListItem.displayName = "ListItem";

// src/v3/CheckmarkListItem/index.tsx
import { jsx as jsx13, jsxs as jsxs5 } from "react/jsx-runtime";
function CheckmarkListItem(props) {
  const { className = "", ...rest } = props;
  return /* @__PURE__ */ jsxs5(ListItem, {
    className: cx5(
      "ui-flex ui-items-center",
      typeof className === "string" ? className : ""
    ),
    ...rest,
    children: [
      /* @__PURE__ */ jsx13(CheckmarkIcon, {
        className: "ui-mr-2"
      }),
      props.children
    ]
  });
}
CheckmarkListItem.displayName = "CheckmarkListItem";

// src/v3/ProductSelectCards/CardAdditionalContent.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
function CardAdditionalContent({
  className,
  content
}) {
  const id = useId();
  const list = content.list;
  if ((list == null ? void 0 : list.length) === 0)
    return null;
  return /* @__PURE__ */ jsx14("div", {
    className: cx6("ui-mt-2", className),
    children: /* @__PURE__ */ jsx14(List, {
      className: "ui-list-inside",
      unstyled: true,
      children: list == null ? void 0 : list.map(({ listItem, ...rest }) => /* @__PURE__ */ jsx14(CheckmarkListItem, {
        ...rest,
        children: listItem
      }, `${listItem}-${id}`))
    })
  });
}
CardAdditionalContent.displayName = "CardAdditionalContent";

// src/v3/ProductSelectCards/CardSizeSelections.tsx
import { cx as cx8 } from "class-variance-authority";

// src/v3/RingSizeSelectors/RingSizeSelectors.tsx
import { useState as useState2 } from "react";

// src/v3/RingSizeSelectors/RingSizeSelector.tsx
import { cx as cx7 } from "class-variance-authority";
import { useRadio } from "react-aria";
import { useContext as useContext3, useId as useId2, useRef } from "react";

// src/icons/LowStockIcon.tsx
import { jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
function LowStockIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs6("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 13,
    viewBox: "0 0 13 13",
    width: 13,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx15("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx15("path", {
        clipRule: "evenodd",
        d: "M6.09245 0.00533208L5.7784 0.0378765C4.25451 0.190021 2.71923 0.975968 1.68106 2.13618C0.53306 3.41924 -0.0502981 5.01147 0.00340016 6.70785C0.0969653 9.63766 2.11065 12.1184 4.96723 12.8246C5.80828 13.0277 6.68128 13.0618 7.5356 12.9249C8.38993 12.788 9.20858 12.4829 9.94408 12.0272C11.3905 11.126 12.4258 9.69346 12.8275 8.03728C12.9675 7.46694 13 7.17649 13 6.502C13 5.82752 12.9675 5.53706 12.8275 4.96672C12.1831 2.3347 10.0157 0.398305 7.31856 0.045199C7.0875 0.0150954 6.24704 -0.0117537 6.09245 0.00533208ZM7.19896 1.2648C8.11287 1.38511 8.97964 1.74152 9.71383 2.2989C9.98151 2.49905 10.5079 3.02545 10.7081 3.29313C11.1645 3.90415 11.5021 4.62257 11.6551 5.31007C11.8292 6.09515 11.8292 6.90886 11.6551 7.69394C11.3183 9.20807 10.2516 10.5522 8.83757 11.2437C8.32778 11.4932 7.78244 11.6623 7.22093 11.7449C6.87596 11.7978 6.13395 11.7978 5.78898 11.7449C4.20407 11.5041 2.86649 10.6392 2.02441 9.30977C1.81728 8.96635 1.6445 8.60335 1.50858 8.22604C1.31478 7.67223 1.21978 7.08869 1.22788 6.502C1.22788 5.65666 1.39142 4.93581 1.75347 4.19135C2.0301 3.62345 2.31161 3.23129 2.77293 2.76998C3.18096 2.34675 3.6626 2.00131 4.1943 1.75052C5.12591 1.29544 6.17142 1.12643 7.19896 1.2648ZM6.26575 3.48351C6.0835 3.54291 5.98018 3.64705 5.92973 3.82116C5.90044 3.92042 5.89719 4.14742 5.90207 5.53869C5.90858 7.1415 5.90858 7.14231 5.95577 7.231C6.01776 7.3446 6.11726 7.43317 6.23728 7.48159C6.34467 7.51902 6.66524 7.51902 6.77263 7.48159C6.89265 7.43317 6.99214 7.3446 7.05414 7.231C7.10133 7.14231 7.10133 7.1415 7.10784 5.53869C7.11435 3.7878 7.11435 3.78618 6.98987 3.63891C6.88898 3.5185 6.78077 3.47863 6.5375 3.47212C6.44676 3.46701 6.35574 3.47082 6.26575 3.48351ZM6.33084 8.56044C6.16806 8.61865 6.03267 8.7352 5.95089 8.88751C5.89215 9.03343 5.88497 9.19502 5.93055 9.34557C5.97454 9.46344 6.0527 9.5655 6.15503 9.6387C6.25735 9.71189 6.37919 9.75289 6.50495 9.75644C6.75148 9.75644 7.00288 9.57745 7.07936 9.34557C7.12494 9.19502 7.11776 9.03343 7.05902 8.88751C6.99167 8.76865 6.89396 8.66982 6.77589 8.60112C6.68151 8.5523 6.42685 8.52871 6.33084 8.56044Z",
        fill: "currentColor",
        fillRule: "evenodd"
      })
    ]
  });
}

// src/v3/consts/stockStatus.ts
var OUT_OF_STOCK = "outOfStock";
var LOW_STOCK = "lowStock";

// src/v3/RingSizeSelectors/RingSizeSelector.tsx
import { jsx as jsx16, jsxs as jsxs7 } from "react/jsx-runtime";
function RingSizeSelector(props) {
  const { disableWhenOutOfStock = true, stockStatus, value } = props;
  const state = useContext3(RadioSelectorGroupContext);
  const ref = useRef(null);
  const id = useId2();
  const { inputProps, isSelected } = useRadio(
    props,
    state,
    ref
  );
  return /* @__PURE__ */ jsxs7("label", {
    "aria-label": value,
    className: cx7(
      "ui-size-full ui-aspect-square ui-rounded-full ui-flex ui-items-center ui-justify-center ui-relative",
      {
        'after:ui-content-[""] after:ui-border-sandstone-500 after:ui-border-2 after:ui-size-full after:ui-rounded-full after:ui-absolute': isSelected,
        "ui-border-gray-400 ui-border": !isSelected,
        "background-linear-to-tl ui-bg-gray-200": stockStatus === OUT_OF_STOCK,
        "!ui-cursor-not-allowed": disableWhenOutOfStock && stockStatus === OUT_OF_STOCK
      }
    ),
    "data-cy": `finish-radio-selector-${value}`,
    "data-stock-status": stockStatus,
    "data-value": value,
    htmlFor: id,
    children: [
      /* @__PURE__ */ jsx16("input", {
        ...inputProps,
        checked: isSelected,
        className: "ui-peer ui-absolute ui-appearance-none",
        "data-cy": `finish-radio-selector-input-${value}`,
        disabled: stockStatus === OUT_OF_STOCK && disableWhenOutOfStock,
        id,
        ref,
        type: "radio"
      }),
      stockStatus === LOW_STOCK && /* @__PURE__ */ jsx16("div", {
        className: "ui-absolute ui--bottom-2 ui--right-2",
        "data-cy": "sizeButtonLowStockIcon",
        children: /* @__PURE__ */ jsx16(LowStockIcon, {
          className: "ui-text-red-600"
        })
      }),
      /* @__PURE__ */ jsx16("div", {
        "data-cy": "size-container",
        children: value
      })
    ]
  });
}
RingSizeSelector.displayName = "RingSizeSelector";

// src/v3/RingSizeSelectors/RingSizeSelectors.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function RingSizeSelectors({
  disableWhenOutOfStock,
  initialSelectedSize,
  label,
  onChange,
  ringSizesData
}) {
  const [selectedSize, setSelectedSize] = useState2(
    initialSelectedSize ? initialSelectedSize.toString() : null
  );
  if (!ringSizesData.length) {
    return null;
  }
  const handleChange = (val) => {
    setSelectedSize(val);
    onChange == null ? void 0 : onChange(val);
  };
  return /* @__PURE__ */ jsx17(RadioSelectorGroup, {
    hideLabel: true,
    label,
    onChange: handleChange,
    value: selectedSize,
    children: /* @__PURE__ */ jsx17("div", {
      className: "ui-grid ui-grid-cols-4 ui-gap-5",
      children: ringSizesData.map((variant) => /* @__PURE__ */ jsx17(RingSizeSelector, {
        "aria-label": String(variant.size),
        disableWhenOutOfStock,
        stockStatus: variant.stockStatus,
        value: String(variant.size)
      }, variant.size))
    })
  });
}
RingSizeSelectors.displayName = "RingSizeSelectors";

// src/v3/ProductSelectCards/CardSizeSelections.tsx
import { jsx as jsx18, jsxs as jsxs8 } from "react/jsx-runtime";
function CardSizeSelections({
  className = "",
  inventoryMessage,
  message,
  ...props
}) {
  return /* @__PURE__ */ jsxs8("div", {
    className: cx8("ui-mt-6", className),
    children: [
      /* @__PURE__ */ jsx18(RingSizeSelectors, {
        ...props
      }),
      message ? /* @__PURE__ */ jsx18(Typography, {
        Element: "p",
        className: "ui-text-gray-450 ui-mt-4 ui-italic",
        color: "unset",
        variant: "eyebrow-small",
        children: message
      }) : null,
      inventoryMessage
    ]
  });
}
CardSizeSelections.displayName = "CardSizeSelections";
export {
  CardAdditionalContent,
  CardSelectorContents,
  CardSizeSelections
};
//# sourceMappingURL=index.mjs.map